import * as THREE from 'three';
import { EventBus } from '../core/EventBus';
import { FlyingDrone } from '../entities/FlyingDrone';
import { HeavyTank } from '../entities/HeavyTank';
import { SniperTower } from '../entities/SniperTower';
import { MegaMech } from '../entities/MegaMech';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { WeaponManager } from '../weapons/WeaponManager';

export interface WaveConfig {
  drones: number;
  tanks: number;
  snipers: number;
  boss: boolean;
}

export interface SpawnPoint {
  position: THREE.Vector3;
  type: 'ground' | 'elevated' | 'air';
}

export type WaveState = 'waiting' | 'spawning' | 'active' | 'complete' | 'rest' | 'gameover';

export class WaveManager {
  private scene: THREE.Scene;
  private projectilePool: ProjectilePool;
  private weaponManager: WeaponManager;
  private playerMesh: THREE.Object3D;

  // Wave state
  private currentWave: number = 0;
  private state: WaveState = 'waiting';
  private stateTimer: number = 0;

  // Timing
  private readonly REST_DURATION = 5; // seconds between waves
  private readonly SPAWN_DELAY = 0.5; // seconds between spawns
  private spawnQueue: Array<{ type: string; point: SpawnPoint }> = [];
  private spawnTimer: number = 0;

  // Active enemies
  private drones: FlyingDrone[] = [];
  private tanks: HeavyTank[] = [];
  private snipers: SniperTower[] = [];
  private boss: MegaMech | null = null;

  // Spawn points
  private spawnPoints: SpawnPoint[] = [];

  // Arena bounds
  private arenaRadius: number = 80;

  constructor(
    scene: THREE.Scene,
    projectilePool: ProjectilePool,
    weaponManager: WeaponManager,
    playerMesh: THREE.Object3D
  ) {
    this.scene = scene;
    this.projectilePool = projectilePool;
    this.weaponManager = weaponManager;
    this.playerMesh = playerMesh;

    this.initSpawnPoints();
    this.setupEventListeners();
  }

  private initSpawnPoints(): void {
    // Ground spawn points in a circle
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      this.spawnPoints.push({
        position: new THREE.Vector3(
          Math.cos(angle) * this.arenaRadius * 0.8,
          0,
          Math.sin(angle) * this.arenaRadius * 0.8
        ),
        type: 'ground',
      });
    }

    // Elevated spawn points for snipers
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
      this.spawnPoints.push({
        position: new THREE.Vector3(
          Math.cos(angle) * this.arenaRadius * 0.9,
          0,
          Math.sin(angle) * this.arenaRadius * 0.9
        ),
        type: 'elevated',
      });
    }

    // Air spawn points for drones
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      this.spawnPoints.push({
        position: new THREE.Vector3(
          Math.cos(angle) * this.arenaRadius * 0.7,
          5,
          Math.sin(angle) * this.arenaRadius * 0.7
        ),
        type: 'air',
      });
    }
  }

  private setupEventListeners(): void {
    EventBus.on('target:destroyed', () => {
      // Check if all enemies are destroyed
      if (this.state === 'active') {
        this.cleanupDeadEnemies();
        if (this.getEnemyCount() === 0 && this.spawnQueue.length === 0) {
          this.onWaveComplete();
        }
      }
    });
  }

  private getWaveConfig(wave: number): WaveConfig {
    // Progressive difficulty scaling
    // Every 5 waves is a boss wave
    const isBossWave = wave > 0 && wave % 5 === 0;

    return {
      drones: 3 + Math.floor(wave * 1.5),
      tanks: wave >= 3 ? Math.floor((wave - 2) / 2) : 0,
      snipers: wave >= 5 ? Math.floor((wave - 4) / 3) : 0,
      boss: isBossWave,
    };
  }

  public startWave(waveNumber?: number): void {
    if (waveNumber !== undefined) {
      this.currentWave = waveNumber;
    } else {
      this.currentWave++;
    }

    const config = this.getWaveConfig(this.currentWave);
    this.state = 'spawning';
    this.spawnQueue = [];

    // Build spawn queue
    this.queueSpawns(config);

    // Emit wave start event
    EventBus.emit('score:added', {
      points: 0,
      reason: `wave_${this.currentWave}_start`,
    });
  }

  private queueSpawns(config: WaveConfig): void {
    const groundPoints = this.spawnPoints.filter(p => p.type === 'ground');
    const elevatedPoints = this.spawnPoints.filter(p => p.type === 'elevated');
    const airPoints = this.spawnPoints.filter(p => p.type === 'air');

    // Queue drones
    for (let i = 0; i < config.drones; i++) {
      const point = airPoints[i % airPoints.length];
      this.spawnQueue.push({ type: 'drone', point });
    }

    // Queue tanks
    for (let i = 0; i < config.tanks; i++) {
      const point = groundPoints[i % groundPoints.length];
      this.spawnQueue.push({ type: 'tank', point });
    }

    // Queue snipers
    for (let i = 0; i < config.snipers; i++) {
      const point = elevatedPoints[i % elevatedPoints.length];
      this.spawnQueue.push({ type: 'sniper', point });
    }

    // Queue boss
    if (config.boss) {
      const point = groundPoints[0];
      this.spawnQueue.push({ type: 'boss', point });
    }

    // Shuffle spawn queue for variety
    this.shuffleArray(this.spawnQueue);
  }

  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  private spawnEnemy(type: string, point: SpawnPoint): void {
    // Add some randomness to spawn position
    const offset = new THREE.Vector3(
      (Math.random() - 0.5) * 5,
      0,
      (Math.random() - 0.5) * 5
    );
    const position = point.position.clone().add(offset);

    switch (type) {
      case 'drone':
        const drone = new FlyingDrone(this.scene, this.projectilePool, {
          position,
          targetRef: this.playerMesh,
        });
        this.drones.push(drone);
        this.weaponManager.registerTarget(drone.getMesh());
        break;

      case 'tank':
        const tank = new HeavyTank(this.scene, this.projectilePool, {
          position,
          targetRef: this.playerMesh,
        });
        this.tanks.push(tank);
        this.weaponManager.registerTarget(tank.getMesh());
        break;

      case 'sniper':
        const sniper = new SniperTower(this.scene, this.projectilePool, {
          position,
          targetRef: this.playerMesh,
        });
        this.snipers.push(sniper);
        this.weaponManager.registerTarget(sniper.getMesh());
        break;

      case 'boss':
        this.boss = new MegaMech(this.scene, this.projectilePool, {
          position,
          targetRef: this.playerMesh,
        });
        this.weaponManager.registerTarget(this.boss.getMesh());
        break;
    }
  }

  private onWaveComplete(): void {
    this.state = 'complete';
    this.stateTimer = 2; // Show "Wave Complete" for 2 seconds

    // Bonus points for completing wave
    const waveBonus = this.currentWave * 500;
    EventBus.emit('score:added', {
      points: waveBonus,
      reason: 'wave_complete',
    });
  }

  public update(dt: number): void {
    switch (this.state) {
      case 'waiting':
        // Waiting for game to start
        break;

      case 'spawning':
        this.spawnTimer -= dt;
        if (this.spawnTimer <= 0 && this.spawnQueue.length > 0) {
          const spawn = this.spawnQueue.shift()!;
          this.spawnEnemy(spawn.type, spawn.point);
          this.spawnTimer = this.SPAWN_DELAY;
        }
        if (this.spawnQueue.length === 0) {
          this.state = 'active';
        }
        break;

      case 'active':
        this.updateEnemies(dt);
        this.cleanupDeadEnemies();

        // Check win condition
        if (this.getEnemyCount() === 0) {
          this.onWaveComplete();
        }
        break;

      case 'complete':
        this.stateTimer -= dt;
        if (this.stateTimer <= 0) {
          this.state = 'rest';
          this.stateTimer = this.REST_DURATION;
        }
        break;

      case 'rest':
        this.stateTimer -= dt;
        if (this.stateTimer <= 0) {
          this.startWave();
        }
        break;

      case 'gameover':
        // Game is over
        break;
    }
  }

  private updateEnemies(dt: number): void {
    for (const drone of this.drones) {
      drone.update(dt);
    }
    for (const tank of this.tanks) {
      tank.update(dt);
    }
    for (const sniper of this.snipers) {
      sniper.update(dt);
    }
    if (this.boss && this.boss.isAlive()) {
      this.boss.update(dt);
    }
  }

  private cleanupDeadEnemies(): void {
    this.drones = this.drones.filter(d => d.isAlive());
    this.tanks = this.tanks.filter(t => t.isAlive());
    this.snipers = this.snipers.filter(s => s.isAlive());
    if (this.boss && !this.boss.isAlive()) {
      this.boss = null;
    }
  }

  public getEnemyCount(): number {
    return (
      this.drones.length +
      this.tanks.length +
      this.snipers.length +
      (this.boss && this.boss.isAlive() ? 1 : 0)
    );
  }

  public getCurrentWave(): number {
    return this.currentWave;
  }

  public getState(): WaveState {
    return this.state;
  }

  public getRestTimer(): number {
    return this.state === 'rest' ? this.stateTimer : 0;
  }

  public getAllEnemies(): THREE.Object3D[] {
    const enemies: THREE.Object3D[] = [];
    for (const drone of this.drones) {
      if (drone.isAlive()) enemies.push(drone.getMesh());
    }
    for (const tank of this.tanks) {
      if (tank.isAlive()) enemies.push(tank.getMesh());
    }
    for (const sniper of this.snipers) {
      if (sniper.isAlive()) enemies.push(sniper.getMesh());
    }
    if (this.boss && this.boss.isAlive()) {
      enemies.push(this.boss.getMesh());
    }
    return enemies;
  }

  public getAllEnemyPositions(): THREE.Vector3[] {
    const positions: THREE.Vector3[] = [];
    for (const drone of this.drones) {
      if (drone.isAlive()) positions.push(drone.getPosition());
    }
    for (const tank of this.tanks) {
      if (tank.isAlive()) positions.push(tank.getPosition());
    }
    for (const sniper of this.snipers) {
      if (sniper.isAlive()) positions.push(sniper.getPosition());
    }
    if (this.boss && this.boss.isAlive()) {
      positions.push(this.boss.getPosition());
    }
    return positions;
  }

  public setGameOver(): void {
    this.state = 'gameover';
  }

  public reset(): void {
    // Clear all enemies
    this.drones = [];
    this.tanks = [];
    this.snipers = [];
    this.boss = null;
    this.spawnQueue = [];
    this.currentWave = 0;
    this.state = 'waiting';
    this.stateTimer = 0;
    this.spawnTimer = 0;
  }

  public begin(): void {
    this.reset();
    this.state = 'rest';
    this.stateTimer = 3; // 3 second countdown before first wave
  }
}
